import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from '@src/hooks/useTranslation';
import { find } from 'lodash';

import useEvent from '@src/hooks/useEvent';
import { getSlotPriceRange, getEventPriceRange, getSelectedTicket } from '@src/utils/event';
import { CURRENCY_SYMBOL } from '@src/const/common';
import { DEFAULT_SLOT_TYPE, DEFAULT_TICKET_TYPE } from '@src/const/event';
import { setCurrentPrice } from '@src/store/actions/event';
import { getIsPriceVisible } from '@src/utils/hardcode';
import { getEvent } from '@src/store/selectors/event';

import Button from '@src/components/common/button';

const PriceButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;

	@media ${props => props.theme.device.lgDesktop} {
		display: grid;
		grid-template-columns: 45fr 55fr;
		grid-column-gap: 16px;
	}

	@media ${props => props.theme.device.desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-column-gap: 16px;
	}
`;

const Prices = styled.div`
	text-align: end;

	@media ${props => props.theme.device.tablet} {
		display: flex;
		align-items: center;
		margin-right: 16px;
	}

	@media ${props => props.theme.device.smTablet} {
		display: flex;
		align-items: center;
		margin-right: 16px;
	}

	@media ${props => props.theme.device.mobile} {
		margin-right: 16px;
	}
`;

const CurrentPrice = styled.div`
	font-size: 32px;
	font-weight: 500;
	color: ${props => props.theme.colors.primary};

	@media ${props => props.theme.device.mobile} {
		font-size: 28px;
	}
`;

const PriceRange = styled.div`
	font-size: 14px;
	color: #A0A0A0;

	@media ${props => props.theme.device.tablet} {
		margin-left: 16px;
	}

	@media ${props => props.theme.device.smTablet} {
		margin-left: 16px;
	}

	@media ${props => props.theme.device.mobile} {
		margin-left: 0;
	}
`;

const CTABtn = styled(Button)`
	font-size: 22px;
	padding: 16px 8px;

	@media ${props => props.theme.device.tablet} {
		width: 200px;
	}

	@media ${props => props.theme.device.smTablet} {
		width: 200px;
	}

	@media ${props => props.theme.device.mobile} {
		width: 170px;
		padding: 12px 8px;
		font-size: 20px;
	}
`;

const getCurrentPrice = ({ minPrice, sortedEventSlots, slotType, ticketType }) => {
	if (slotType === DEFAULT_SLOT_TYPE) {
		return minPrice;
	} else if ((slotType !== DEFAULT_SLOT_TYPE) && ticketType === DEFAULT_TICKET_TYPE) {
		const selectedSlot = find(sortedEventSlots, slot => slot.id === slotType);

		return getSlotPriceRange(selectedSlot.ticket_info).minPrice;
	}

	const selectedTicket = getSelectedTicket({ slotId: slotType, ticketId: ticketType, sortedEventSlots });

	return selectedTicket.price;
};

interface ITicketSelection {
	className?: string;
	slotType: string;
	ticketType: string;
	buttonText: string;
	isDisable?: boolean;
	handleButtonClick: () => void;
}

const PriceButton = ({
	className, slotType, ticketType, buttonText, isDisable = false, handleButtonClick,
}: ITicketSelection): JSX.Element => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { sortedEventSlots } = useEvent();
	const { minPrice, maxPrice } = getEventPriceRange(sortedEventSlots);
	const event = useSelector(getEvent);
	const { id: eventId } = event || {};
	const isPriceVisible = getIsPriceVisible(eventId);

	const currentPrice = getCurrentPrice({ minPrice, sortedEventSlots, slotType, ticketType });
	const showPriceRange = minPrice !== maxPrice;
	const isFreeEvent = (minPrice === 0) && (maxPrice === 0);

	useEffect(() => {
		dispatch(setCurrentPrice({ currentPrice }));
	}, [currentPrice]);

	return (
		<PriceButtonWrapper className={className}>
			<Prices>
				{
					isPriceVisible ? (
						<>
							<CurrentPrice>
								{isFreeEvent ? t('common:free') : `${CURRENCY_SYMBOL} ${currentPrice.toLocaleString()}`}
							</CurrentPrice>
							{showPriceRange && (
								<PriceRange>
									{`${CURRENCY_SYMBOL} ${minPrice.toLocaleString()} ~ ${maxPrice.toLocaleString()}`}
								</PriceRange>
							)}
						</>
					) : (
						<CurrentPrice>{t('common:consultation')}</CurrentPrice>
					)
				}
			</Prices>
			<CTABtn isDisable={isDisable} handleClick={handleButtonClick}>{buttonText}</CTABtn>
		</PriceButtonWrapper>
	);
};

export default PriceButton;
